import { Auth } from "../../api";
import setup from "../../setup";
import { RouteChecker, route_names } from "../routes";

/**
 * Representing auth gard that should be applied to all application routes.
 */
class AuthGuard
{
    /**
     * Initializing AuthGuard.
     */
    constructor()
    {
        /**
         * Checking route access type.
         * @type {RouteChecker}
         */
        this.route_checker = new RouteChecker;

        /**
         * Auth API.
         * @type {Auth}
         */
        this.auth_api = new Auth();
        
        /**
         * Telling if user was retrieved from auth API.
         * @type {Boolean}
         */
        this.user_retrieved = false;
    }

    /**
     * Telling if user is considered as authenticated.
     * @returns {Boolean}
     */
    isUserAuthenticated()
    {
        const { token, user } = setup.store.state;
        
        return !!(token && user);
    }

    /**
     * Trying to authenticate current user.
     * @returns {import("./types").AuthGardPotentialRedirect}
     */
    async authenticateUser()
    {
        setup.startLoading();
        
        /** @type {import("./types").AuthGardPotentialRedirect} */
        const status = { success: false };

        const response = await this.auth_api.getAuthenticatedUserOrRedirectResponse();
        this.user_retrieved = true;
        
        // if error.
        if (!response) {
            setup.setToken(null);
            return status;
        }

        // if redirect response.
        if (response.type) {
            setup.setToken(null);
            return { ...status, redirect: this.redirect.bind(this, response.redirect)};
        }

        if ( response && response.user && response.user.worksite_launch_seen == false ) {
            return { ...status, redirect: this.redirect.bind(this, 'https://www.trustup.pro')};
        }

        // Success.
        this.setAuthenticatedUser(response);
        setup.setMeilisearch(response.meilisearch);
        return { ...status, success: true };
    }

    /**
     * Redirecting to expected location.
     * 
     * @param {string} url
     * @return {Promise<void>}
     */
    redirect(url)
    {
        return Promise.resolve(window.location.replace(url));
    }

    /**
     * Setting authenticated user in app.
     * @param {import("../../api/auth/types").AuthenticatedUserResponseInterface} response
     * @return {AuthGuard}
     */
    setAuthenticatedUser(response)
    {
        const { token, user, apps } = response;

        setup.setApps(apps)
            .setUser(user)
            .setToken(token);

        return this;
    }

    /**
     * Guard that should be applied to router
     * @param {import("vue-router").Route} to Route where we're going
     * @param {import("vue-router").Route} from Route where we were
     * @param {import("vue-router").NavigationGuardNext} next Going to next step
     * @returns {any}
     */
    async guard(to, from, next)
    {
        const { redirect } = to;

        // User not retrieved yet.
        if (!this.isUserAuthenticated() && !this.user_retrieved) {
            const { redirect: redirectFromAuth } = await this.authenticateUser();
            // Redirect to expected location.
            if (redirectFromAuth) {
                return redirectFromAuth();
            }
        }

        // restricted route and user authenticated.
        if (this.route_checker.isRestricted(to) && this.isUserAuthenticated()) {
            return this.redirectToHomepage();
        }

        // Protected route and user unauthenticated.
        if (this.route_checker.isProtected(to) && !this.isUserAuthenticated()) {
            return this.redirectToLogin();
        }

        // Redirect to right route before checking anything else.
        if (redirect) {
            return setup.router.replace(redirect);
        }

        // Checking if page is account selection.
        const { redirect: redirectToAccount } = this.checkAccountSelection(to);
        if (redirectToAccount) {
            return redirectToAccount();
        }

        // Checking if user is having access to expected account (if applicable).
        const { redirect: redirectFromUnauthorizedAccount } = this.checkAccountAccess(to);
        // Trying to access unauthorized account => redirecting.
        if (redirectFromUnauthorizedAccount) {
            return redirectFromUnauthorizedAccount();
        }

        this.continue(to, from, next);
    }

    /**
     * Redirecting user to specific account or just letting him go through.
     * 
     * @param {import("vue-router").Route} to
     * @returns {import("./types").AuthGardPotentialRedirect}
     */
     checkAccountSelection(to)
     {
         const potentialRedirect = this.potentialRedirect();
         
         // if we can't check available accounts => go through.
         if (!setup.current_app) return potentialRedirect;
         
         const { accounts } = setup.current_app;
 
         // Not concerning account selection or having several accounts => go through.
         if (
             !this.route_checker.isAccountSelection(to)
             || accounts.length > 1
         ) {
             return potentialRedirect;
         }
 
         return {
             ...potentialRedirect,
             success: false,
             redirect: this.redirectToAccount.bind(this, accounts[0])
         };
     }

    /**
     * Checking if user can access account (if applicable).
     * 
     * If access granted, set account too.
     * if not, returning a redirect response.
     * 
     * @param {import("vue-router").Route} to
     * @returns {import("./types").AuthGardPotentialRedirect}
     */
    checkAccountAccess(to)
    {
        const { account: uuid } = to.params;
        const potentialRedirect = this.potentialRedirect();
        
        // Route is not concerning any account => go through.
        if ( ! uuid ) {
            return potentialRedirect;
        }
        
        const currentApp = setup.getCurrentApp();

        /**
         * There is no current app
         * It should never happens but just in case... 🤷‍♂️ => Redirecting to auth.
         */
        if ( ! currentApp ) {
            return { ...potentialRedirect, success: false, redirect: this.redirectToLogin };
        }

        const account = currentApp.accounts.find((account) => account.uuid === uuid);

        // Trying to access unauthorized account => redirecting to homepage.
        if ( ! account ) {
            return { ...potentialRedirect, success: false, redirect: this.redirectToHomepage }
        }

        if ( ! setup.store.state.account ) {
            setup.setAccount(account);
        }

        return potentialRedirect;
    }

    /**
     * Giving a default potential redirect.
     * @returns {import("./types").AuthGardPotentialRedirect}
     */
    potentialRedirect()
    {
        return { success: true };
    }

    /**
     * Redirecting from a restricted route.
     * @returns {Promise<void>}
     */
    redirectToHomepage()
    {
        return setup.router.replace({ name: route_names.HOMEPAGE });
    }

    /**
     * Redirecting from a protected route.
     * @returns {Promise<void>}
     */
    redirectToLogin()
    {
        const base_url = `${setup.auth_url}/login`;
        const parameters = { 
            app: setup.current_app_key,
            redirect: window.location
        }; 

        return this.redirect(`${base_url}?${new URLSearchParams(parameters).toString()}`);
    }

    /**
     * Redirecting to given account.
     * @param {import("../../api/auth/types").AuthenticatedUserResponseAccountInterface} account
     * @returns {Promise<void>}
     */
    redirectToAccount(account)
    {
        return setup.router.replace({ name: route_names.ACCOUNTS_SHOW, params: { account: account.uuid } });
    }

    /**
     * Setting up some options before letting router go through.
     * @param {import("vue-router").Route} to Route where we're going
     * @param {import("vue-router").Route} from Route where we were
     * @param {Function} next Going to next step
     * @returns {any}
     */
    continue(to, from, next)
    {
        this.setLayoutOptions(to)
            .setSidebarSection(to)
            .setMetas(to);

        setup.stopLoading();
        
        return next();
    }

    /**
     * Setting layout options according to given route.
     * @param {import("vue-router").Route} to Route where we're going
     * @returns {AuthGuard}
     */
    setLayoutOptions(to)
    {
        const { layout = {} } = to.meta;
        setup.store.dispatch('LAYOUT/UPDATE', layout);

        return this;
    }

    /**
     * Setting sidebar section for upcoming route.
     * @param {import("vue-router").Route} to Route where we're going
     * @returns {AuthGuard}
     */
     setSidebarSection(to)
    {
        /** @type {?import("../routes/types").RouteSidebarInteface} */
        const section = to?.meta.sidebar_section;

        if (!section) return this;

        const value = typeof section === "string" ? section : section(to);
        setup.store.dispatch('SIDEBAR/SET_SECTION', value);

        return this;
    }

    /**
     * Setting page metas based on given route.
     * @param {import("vue-router").Route} to Route where we're going
     * @returns {AuthGuard}
     */
    setMetas(to)
    {
        const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);
        const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);

        if (nearestWithTitle) {
            let title = nearestWithTitle.meta.title;
            for (let parameter in to.params) {
                title = title.replace(`:${parameter}`, to.params[parameter]);
            }
            document.title = title;
        }

        Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

        if (!nearestWithMeta) return this;

        nearestWithMeta.meta.metaTags.map(tagDef => {
                const tag = document.createElement('meta');

                Object.keys(tagDef).forEach(key => {
                    tag.setAttribute(key, tagDef[key]);
                });

                tag.setAttribute('data-vue-router-controlled', '');

                return tag;
            })
            .forEach(tag => document.head.appendChild(tag));
        
        return this;
    }

    /**
     * Applying guard to router.
     * @returns {import("vue-router").Router}
     */
    apply()
    {
        const { router } = setup;
        router.beforeEach((to, from, next) => this.guard(to, from, next));

        return router;
    }
}

export default AuthGuard;